/* @jsx h */
import { defineComponent, h, onBeforeUnmount, ref, watch } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { ACCEPTED_TYPES, isAcceptedImage, MAX_PAGES } from "./prepareDocument";

type Props = {
  files: File[];
  onChange: (files: File[]) => void;
};

const ACCEPT = ACCEPTED_TYPES.join(",");

// Drop photos (or take one on a phone): up to four, one per page.
export const ScanDropzone = defineComponent<Props>({
  name: "ScanDropzone",
  props: ["files", "onChange"],
  setup(props) {
    const { t } = useLocalization();
    const over = ref(false);
    // Files dropped that aren't photos (e.g. PDFs).
    const rejected = ref(0);
    const fileInput = ref<HTMLInputElement | null>(null);
    const cameraInput = ref<HTMLInputElement | null>(null);
    const thumbs = ref<string[]>([]);

    watch(
      () => props.files,
      (files) => {
        thumbs.value.forEach((u) => u && URL.revokeObjectURL(u));
        thumbs.value = files.map((f) => URL.createObjectURL(f));
      },
      { immediate: true },
    );
    onBeforeUnmount(() => thumbs.value.forEach((u) => u && URL.revokeObjectURL(u)));

    const add = (list: FileList | null | undefined) => {
      const all = Array.from(list ?? []);
      const picked = all.filter(isAcceptedImage);
      rejected.value = all.length - picked.length;
      if (!picked.length) return;
      props.onChange([...props.files, ...picked].slice(0, MAX_PAGES));
    };

    const remove = (i: number) => props.onChange(props.files.filter((_, j) => j !== i));

    return () => (
      <div class="space-y-3">
        <div
          class={[
            "flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-10 text-center transition",
            over.value ? "border-primary bg-primary/5" : "border-base-300 bg-base-200/40 hover:border-base-content/30",
          ].join(" ")}
          onDragover={(e: DragEvent) => {
            e.preventDefault();
            over.value = true;
          }}
          onDragleave={() => (over.value = false)}
          onDrop={(e: DragEvent) => {
            e.preventDefault();
            over.value = false;
            add(e.dataTransfer?.files);
          }}
        >
          <span class="material-symbols-outlined text-5xl text-primary">document_scanner</span>
          <div>
            <div class="font-semibold">{t("inventory.scan.drop_title")}</div>
            <div class="mt-1 text-sm text-base-content/60">{t("inventory.scan.drop_help")}</div>
          </div>
          <div class="flex flex-wrap justify-center gap-2">
            <button class="btn btn-neutral btn-sm" onClick={() => fileInput.value?.click()}>
              <span class="material-symbols-outlined text-base">upload_file</span>
              {t("inventory.scan.choose_file")}
            </button>
            <button class="btn btn-ghost btn-sm" onClick={() => cameraInput.value?.click()}>
              <span class="material-symbols-outlined text-base">photo_camera</span>
              {t("inventory.scan.take_photo")}
            </button>
          </div>
          <input
            ref={fileInput}
            type="file"
            class="hidden"
            multiple
            accept={ACCEPT}
            onChange={(e: Event) => {
              add((e.target as HTMLInputElement).files);
              (e.target as HTMLInputElement).value = "";
            }}
          />
          <input
            ref={cameraInput}
            type="file"
            class="hidden"
            accept="image/*"
            capture="environment"
            onChange={(e: Event) => {
              add((e.target as HTMLInputElement).files);
              (e.target as HTMLInputElement).value = "";
            }}
          />
        </div>

        {rejected.value > 0 && (
          <div class="alert alert-warning py-2 text-sm">{t("inventory.scan.photos_only")}</div>
        )}
        {props.files.length > 0 && (
          <div class="flex flex-wrap gap-3">
            {props.files.map((f, i) => (
              <div key={`${f.name}-${i}`} class="group relative h-28 w-24 overflow-hidden rounded-lg border border-base-300 bg-base-100">
                <img src={thumbs.value[i]} class="h-full w-full object-cover" alt={f.name} />
                <span class="absolute bottom-1 left-1 rounded bg-base-content/70 px-1 text-[10px] text-base-100">
                  {t("inventory.scan.page_n").replace("{n}", String(i + 1))}
                </span>
                <button
                  class="btn btn-circle btn-xs absolute right-1 top-1 opacity-80 group-hover:opacity-100"
                  aria-label={t("inventory.scan.remove")}
                  onClick={() => remove(i)}
                >
                  <span class="material-symbols-outlined text-sm">close</span>
                </button>
              </div>
            ))}
            {props.files.length < MAX_PAGES && (
              <button
                class="flex h-28 w-24 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-base-300 text-xs text-base-content/60 hover:border-primary hover:text-primary"
                onClick={() => fileInput.value?.click()}
              >
                <span class="material-symbols-outlined">add</span>
                {t("inventory.scan.add_page")}
              </button>
            )}
          </div>
        )}
      </div>
    );
  },
});
